import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { useParallaxY } from "../../hooks/useParallaxY";
import AnimatedText from "../AnimatedText";

const faqs = [
  {
    question: "What types of freezers do you offer?",
    answer: "We offer various types including chest freezers, upright freezers, and commercial-grade models."
  },
  {
    question: "What is the difference between a chest freezer and an upright freezer?",
    answer: "Chest freezers typically offer more storage space, while upright freezers provide easier access and organization."
  },
  {
    question: "What sizes or capacities are available?",
    answer: "Sizes vary from compact units to industrial models. Capacity is listed in liters, gallons, and wattage specs."
  },
  {
    question: "Are your freezers energy efficient?",
    answer: "Yes, all our models meet energy-saving standards to help reduce your electricity costs."
  },
  {
    question: "Do your freezers come with a warranty? What does it cover?",
    answer: "Yes. Our warranties cover compressor failure, manufacturing defects, and selected parts."
  },
  {
    question: "What is the expected lifespan of your freezers?",
    answer: "On average, our freezers last 10–15 years with regular maintenance and proper usage."
  },
  {
    question: "Do your freezers work in garages or outdoor settings?",
    answer: "Many models are rated for outdoor use and function well in garage conditions with proper ventilation."
  },
  {
    question: "Can I convert the freezer into a chiller?",
    answer: "Some models support temperature adjustments but full conversion is only recommended for specific units."
  },
];

const FAQSection: React.FC = () => {
  const navigate = useNavigate();
  const faqSectionRef = useRef<HTMLElement>(null);
  const faqBlobY1 = useParallaxY(faqSectionRef, [-60, 60]);
  const faqBlobY2 = useParallaxY(faqSectionRef, [60, -60]);

  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const toggle = (index: number) => {
    setActiveIndex(index === activeIndex ? null : index);
  };

  return (
    <motion.section
      ref={faqSectionRef}
      id="faq"
      className="min-h-screen py-20 px-6 md:px-20 relative bg-white dark:bg-[#121212]"
    >
      <div className="relative w-full bg-[#F5F5F5] dark:bg-[#1E1E1E] rounded-[40px] overflow-hidden border border-gray-200 dark:border-[#2C2C2C] shadow-sm">
        <div className="absolute inset-0 z-0">
          <motion.div className="absolute top-[-10%] left-[-10%] w-[50%] h-[80%] rounded-full bg-[#172b81] blur-[100px] opacity-[0.12] dark:opacity-[0.15]" style={{ y: faqBlobY1 }} />
          <motion.div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[80%] rounded-full bg-[#e1ae48] blur-[100px] opacity-[0.15] dark:opacity-[0.15]" style={{ y: faqBlobY2 }} />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_transparent_0%,_rgba(255,255,255,0.2)_100%)] dark:bg-[radial-gradient(circle_at_center,_transparent_0%,_#121212_100%)]"></div>
        </div>

        <div className="relative z-10 py-16 px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: false }}
            className="text-center mb-12"
          >
            <AnimatedText
              as={motion.h2}
              className="text-4xl font-bold mb-2 text-black dark:text-[#E5E7EB]"
              text="Got any questions?"
            />
            <div className="w-16 h-1 bg-[#172b81] dark:bg-[#e1ae48] mx-auto mb-4 rounded-full"></div>
            <p className="text-xl text-gray-500 dark:text-[#9CA3AF]">We've got answers.</p>
          </motion.div>

          {/* items-start keeps each card at its own height — without it, grid
              rows stretch and an expanded card drags its neighbour taller. */}
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto items-start">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                className={`bg-white/80 dark:bg-[#1E1E1E] backdrop-blur-md rounded-xl shadow-sm border border-gray-200 dark:border-[#2C2C2C] transition-all duration-300 ${activeIndex === index ? "p-6" : "p-4"}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: false }}
              >
                <button
                  className="w-full flex justify-between items-center text-left text-base md:text-lg font-medium text-black dark:text-[#E5E7EB]"
                  onClick={() => toggle(index)}
                >
                  <span>{faq.question}</span>
                  <span className={`text-xl transition-colors ${activeIndex === index ? "text-[#172b81] dark:text-[#e1ae48]" : ""}`}>
                    {activeIndex === index ? "✕" : "+"}
                  </span>
                </button>
                {activeIndex === index && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mt-4 text-sm md:text-base text-gray-600 dark:text-[#9CA3AF] border-t border-gray-100 dark:border-gray-800 pt-4"
                  >
                    {faq.answer}
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button
              className="group bg-[#172b81] dark:bg-white text-white dark:text-black px-6 py-3 rounded-full border border-transparent hover:bg-transparent hover:text-[#172b81] dark:hover:text-white hover:border-[#172b81] dark:hover:border-white transition flex items-center gap-2 mx-auto shadow-lg"
              onClick={() => navigate('/faqs')}
            >
              <span>Show More</span>
              <ArrowOutwardIcon
                className="text-white dark:text-black group-hover:text-[#172b81] dark:group-hover:text-white transition-colors duration-300"
                fontSize="small"
              />
            </button>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default FAQSection;
